/**
 * Impersonation session for Zporti operator API calls.
 * Persisted to sessionStorage (survives refresh, cleared on tab close).
 * Kept separate from `useImpersonation` so `apiClient` can read the token without a circular import through React hooks.
 * Subscribers allow a root-level banner to reflect active impersonation after navigation.
 */

export interface ImpersonationSession {
  token: string
  targetUserId: string
  targetLabel: string
  /** Token expiry (ms epoch). Session is discarded if expired on restore. */
  expiresAt: number
}

const STORAGE_KEY = 'zporti_impersonation_session'

const listeners = new Set<() => void>()

function readFromStorage(): ImpersonationSession | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: ImpersonationSession = JSON.parse(raw)
    if (!parsed.token || !parsed.targetUserId || typeof parsed.expiresAt !== 'number') return null
    if (Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeToStorage(value: ImpersonationSession | null): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (value) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Storage full or unavailable — fall back to memory-only
  }
}

let session: ImpersonationSession | null = readFromStorage()

function notify(): void {
  listeners.forEach((cb) => {
    cb()
  })
}

export function subscribeImpersonationSession(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function getImpersonationSession(): ImpersonationSession | null {
  return session
}

export function setImpersonationSession(next: ImpersonationSession | null): void {
  session = next
  writeToStorage(next)
  notify()
}

export function getImpersonationToken(): string | null {
  return session?.token ?? null
}
